import { type FunctionComponent, type PropsWithChildren, useContext } from 'react';
import { AlertNotificationContext, type AlertNotificationType } from '../../../contexts/alert-notification.context';
import CheckCircleIcon from '../../svgs/check-circle.svg';
import CloseIcon from '../../svgs/close.svg';
import InfoIcon from '../../svgs/info.svg';
import LockIcon from '../../svgs/lock.svg';
import ReportIcon from '../../svgs/report.svg';
import WarningIcon from '../../svgs/warning.svg';

const fontClassMap = {
	SMALL: 'f7',
	MEDIUM: 'f6',
	LARGE: 'f5',
	GLOBAL: 'f5'
};

const iconClassMap = {
	SMALL: 'f5',
	MEDIUM: 'f4',
	LARGE: 'f4',
	GLOBAL: 'f4'
};

const closeIconMarginTopClassMap = {
	SMALL: 3,
	MEDIUM: 3,
	LARGE: 6,
	GLOBAL: 6
};

const paddingClassMap = {
	SMALL: 'pa2',
	MEDIUM: 'pa2',
	LARGE: 'pa3',
	GLOBAL: 'pv3 pl3'
};

const iconPaddingClassMap = {
	SMALL: 'pr2 pt2',
	MEDIUM: 'pr2 pt2',
	LARGE: 'ph3 pt3',
	GLOBAL: 'ph3 pt3'
};

const iconPaddingTopClassMap = {
	SMALL: 7,
	MEDIUM: 8,
	LARGE: 18,
	GLOBAL: 18
};

export type AlertNotificationProps = {
	alertType: AlertNotificationType;
	size: 'SMALL' | 'MEDIUM' | 'LARGE' | 'GLOBAL';
	showCloseButton?: boolean;
	alignCenter?: boolean;
	onClose?: () => void;
	roundedDirection?: 'top' | 'all';
};

const iconsMap = {
	danger: <ReportIcon data-testid="ReportIcon" />,
	warning: <WarningIcon data-testid="WarningIcon" />,
	info: <InfoIcon />,
	success: <CheckCircleIcon />,
	grey: <LockIcon />
};

/**
 * The alert notification component should be used to display an alert banner above the header of the page.
 */
// TODO: EFDC-33198 - refactor to reduce cognitive complexity
export const AlertNotification: FunctionComponent<PropsWithChildren<AlertNotificationProps>> = ({
	alertType,
	size,
	showCloseButton,
	alignCenter,
	children,
	onClose,
	roundedDirection = 'all'
}) => {
	const { closeNotification } = useContext(AlertNotificationContext);

	const closeClick = () => {
		onClose?.();

		closeNotification();
	};

	const alertBackgroundColor = alertType === 'grey' ? 'bg-theme-grey-lighter' : `bg-theme-${alertType}-light`;

	const borderRadius = roundedDirection === 'top' ? 'br2 br--top' : 'br2';

	return children ? (
		<div
			className={
				// requires pattern in postcss.config.js
				`${alertBackgroundColor} ba b--theme-${alertType} flex items-stretch ${borderRadius}`
			}
			data-testid="alert-notification"
			style={size === 'GLOBAL' ? { boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)' } : undefined}>
			<div
				className={`theme-white ${roundedDirection === 'top' ? '' : 'br2 br--left'} bg-theme-${alertType} ${
					iconClassMap[size]
				} ph2 ${iconPaddingClassMap[size]}`}
				style={{
					paddingTop: iconPaddingTopClassMap[size],
					transform: 'translateX(-1px)',
					borderTopLeftRadius: roundedDirection === 'top' ? '0.25rem' : undefined
				}}>
				{iconsMap[alertType]}
			</div>

			<div
				className={
					// requires pattern in postcss.config.js
					`pt w-100 ma0 ${alignCenter ? 'tc' : ''} theme-${alertType === 'grey' ? 'grey-dark' : alertType} ${
						paddingClassMap[size]
					} lh-copy self-start ${fontClassMap[size]}`
				}>
				{children}
			</div>
			{showCloseButton && (
				<button
					onClick={closeClick}
					type="button"
					className={`close pa0 b--none bg-transparent pointer flex ${iconPaddingClassMap[size]} lh-copy self-start ml5`}
					data-dismiss="alert"
					aria-label="Close"
					style={{ marginTop: closeIconMarginTopClassMap[size] }}>
					<CloseIcon className={`f7 theme-${alertType}`} data-testid="CloseIcon" />
				</button>
			)}
		</div>
	) : null;
};

/**
 * An alert notification that is fixed at the top of the window.
 */
export const GlobalAlertNotification: FunctionComponent = () => {
	const { message, type, isDialogOpen } = useContext(AlertNotificationContext);
	return message ? (
		<div className="absolute top-1 mt2-ns ph3 ph0-ns w-100 h-100 disableClickEvents">
			<div
				className={`sticky ${isDialogOpen ? 'flex vh-75 flex-column justify-end' : ''} top-2 z-5`}
				data-testid={'global-alert-notification'}
				style={{ width: 'fit-content', margin: '0 auto', pointerEvents: 'auto' }}>
				<AlertNotification alertType={type} size={'GLOBAL'} showCloseButton={true}>
					{message}
				</AlertNotification>
			</div>
		</div>
	) : null;
};
